import { BLEConnection, BLEDevice, BLEProvider } from '@/ble/types'
import {
  BleConnectionStatus,
  BleProviderErrorCodes,
  BleProviderEventTypes
} from '@/ble/constants'
import { BleDeviceTypes } from '@/constants/ble'
import BleProvider, { logger } from '@/ble'
import { iOSConnectSuccessResults } from '@/types/ble'
import { isPlatformWindows } from '@/utils/cordova'
import { Window } from '@/types/cordova'

const enum BleConnectionRunningStatus {
  RUNNING,
  STOP
}

export default class BleConnection implements BLEConnection {
  readonly CONNECT_RETRY_LIMIT = Infinity
  readonly RETRY_WAIT_TIME = 5000
  readonly RETRY_WAIT_TIME_DISCONNECT_FROM_DEVICE = 5000
  readonly CONNECTION_TIMEOUT = 5000
  readonly WINDOWS_WAIT_FOR_RECEIVE_VALUE = 5000
  readonly WINDOWS_WAIT_FOR_RECEIVE_VALUE_D5 = 30000

  readonly address!: string
  readonly deviceType!: BleDeviceTypes

  provider!: BLEProvider

  connectionStatus!: BleConnectionStatus
  runningStatus = BleConnectionRunningStatus.STOP
  processing = false
  params = {}

  constructor(
    device: BLEDevice,
    connectionStatus = BleConnectionStatus.DISCONNECTED,
    provider = BleProvider
  ) {
    this.address = device.address
    this.deviceType = device.deviceType
    this.connectionStatus = connectionStatus
    this.provider = provider

    this.provider.addListener(
      BleProviderEventTypes.CONNECT,
      this.handleConnect.bind(this)
    )
    this.provider.addListener(
      BleProviderEventTypes.DISCONNECT,
      this.handleDisconnect.bind(this)
    )
    this.provider.addListener(
      BleProviderEventTypes.RECEIVE_VALUE,
      async readVal => {
        if (!isPlatformWindows()) return
        if (readVal.deviceType === BleDeviceTypes.ECG_SERVICE) return
        if (readVal.deviceType == this.deviceType) {
          this.handleDisconnect(device, true)
        }
      }
    )
    this.provider.setDevice(device)

    const waitTime =
      this.deviceType === BleDeviceTypes.SPO2_MONITOR_MIGHTY_SAT
        ? this.WINDOWS_WAIT_FOR_RECEIVE_VALUE_D5
        : this.WINDOWS_WAIT_FOR_RECEIVE_VALUE
    const timerId = setInterval(() => {
      const cordova = (window as Window).cordova
      if (!cordova) {
        return
      }
      if (!isPlatformWindows()) {
        clearInterval(timerId)
        return
      }
      if (!this.processing) {
        this.handleDisconnect(device, true)
      }
    }, waitTime)
  }

  setStatus(connectionStatus: BleConnectionStatus) {
    this.connectionStatus = connectionStatus
  }

  async run(params = {}, manual = false) {
    this.processing = manual
    logger('[BLE_CONNECTION] EXECUTE_RUN: ' + this.address)

    if (this.isRunning()) {
      console.warn('Connectin aleady activated! ' + this.address)

      if (this.isPending()) {
        throw new Error('Connction is pending ' + this.address)
      }

      return
    }

    this.runningStatus = BleConnectionRunningStatus.RUNNING
    this.params = params

    return this.attempt(0, params)
  }

  async attempt(retry = 0, params = {}) {
    logger(
      '[BLE_CONNECTION] EXECUTE_ATTEMPT: retry: ' +
        retry +
        ' address: ' +
        this.address
    )
    if (retry > this.CONNECT_RETRY_LIMIT) {
      this.setStatus(BleConnectionStatus.DISCONNECTED)
      throw Error(
        'BLE connection retry times exceeded! address: ' + this.address
      )
    }

    if (!this.isRunning()) {
      if (this.isDisconnected()) {
        try {
          await this.provider.disconnect(this.address)
        } catch (error) {
          if (typeof error === 'object' && error.error === 'isDisconnected') {
            console.error('The below error is ignored. address:' + this.address)
            console.error(error)
          } else {
            throw error
          }
        }
      }
      console.log(
        'Stop to attempt to establish connection. address: ' + this.address
      )
      return
    }

    if (this.isPending() && retry === 0) {
      console.warn(
        'Connecting request is aborted because the connection to the device of the address you specified is pending. address: ' +
          this.address
      )
      return
    }

    this.setStatus(BleConnectionStatus.PENDING)

    try {
      const result = await new Promise<iOSConnectSuccessResults | void>(
        (resolve, reject) => {
          this.provider.connect(this.address, params).then(resolve, reject)
        }
      )

      this.setStatus(BleConnectionStatus.CONNECTED)
      logger(
        '[BLE_CONNECTION] EXECUTE_ATTEMPT (CONNECTED): retry: ' +
          retry +
          ' address: ' +
          this.address
      )

      return result
    } catch (error) {
      console.error(error)
      console.log('Try reconnect... address: ' + this.address)

      if (
        typeof error !== 'string' &&
        (error.code === BleProviderErrorCodes.TIMEOUT ||
          (error.message || '').indexOf(
            'Device previously connected, reconnect or close for new connection. address: ' +
              this.address
          ) > -1)
      ) {
        console.log(
          'First, try disconnect the last connection because the connection is still pending or connected. address: ' +
            this.address
        )
        try {
          await this.provider.disconnect(this.address)
        } catch (error) {
          console.error('The below error is ignored.')
          console.error(error)
        }
      }

      return await new Promise<iOSConnectSuccessResults | void>(
        (resolve, reject) => {
          setTimeout(() => {
            this.attempt(++retry, params).then(resolve, reject)
          }, this.RETRY_WAIT_TIME)
        }
      )
    }
  }

  async stop() {
    logger('[BLE_CONNECTION] EXECUTE_STOP: ' + this.address)
    this.runningStatus = BleConnectionRunningStatus.STOP

    try {
      await this.provider.disconnect(this.address)
      this.setStatus(BleConnectionStatus.DISCONNECTED)
    } catch (error) {
      if (typeof error === 'object' && error.error === 'isDisconnected') {
        this.setStatus(BleConnectionStatus.DISCONNECTED)
      }
      throw error
    }
  }

  isDisconnected() {
    return this.connectionStatus === BleConnectionStatus.DISCONNECTED
  }

  isLogicallyConnected() {
    return this.connectionStatus === BleConnectionStatus.LOGICALLY_CONNECTED
  }

  isPending() {
    return this.connectionStatus === BleConnectionStatus.PENDING
  }

  isConnected() {
    return this.connectionStatus === BleConnectionStatus.CONNECTED
  }

  isRunning() {
    return this.runningStatus === BleConnectionRunningStatus.RUNNING
  }

  isStop() {
    return this.runningStatus === BleConnectionRunningStatus.STOP
  }

  setProcessing(processing: boolean) {
    this.processing = processing
  }

  handleConnect(device: BLEDevice) {
    if (device.address !== this.address) {
      return
    }

    this.setStatus(BleConnectionStatus.CONNECTED)
  }

  handleDisconnect(device: BLEDevice, fromRemoteDevice: boolean) {
    if (device.address !== this.address) {
      return
    }

    logger(
      '[BLE_CONNECTION] handleDisconnect: address: ' +
        this.address +
        ' fromRemoteDevice: ' +
        fromRemoteDevice
    )
    if (!fromRemoteDevice) {
      return
    }

    this.setStatus(BleConnectionStatus.LOGICALLY_CONNECTED)

    setTimeout(() => {
      this.attempt()
    }, this.RETRY_WAIT_TIME_DISCONNECT_FROM_DEVICE)
  }
}
